import {
  directoryRelativeUrl,
  isInDirectory,
  join,
  rootName,
} from "@tsmono/util";

import type { LogListingRow } from "../../log_data";
import { logsUrl, tasksUrl } from "../routing/url";

import type { FileLogItem } from "./LogItem";
import type { LogsPanelMode } from "./LogsPanel";

/** The view inputs that determine a log's file item (and whether it has one). */
export interface FileLogItemView {
  mode: LogsPanelMode;
  logDir: string;
  /** The directory being listed (folder mode lists it; tasks mode ignores it
   *  and lists the whole `logDir`). */
  currentDir: string;
  showRetriedLogs: boolean;
}

/**
 * The path-derived part of a file item — id, display name, and url — or
 * `undefined` when the path isn't a file row of the view (folder mode: not
 * directly in the current directory; it displays through its folder
 * instead). Pure path logic: display toggles like retried-hiding live in
 * {@link fileLogItem}, so overview facts that need pre-hide membership (and
 * URL derivations that have only a name) can use this directly.
 */
export const fileLogIdentity = (
  name: string,
  view: FileLogItemView
): { id: string; name: string; url: string } | undefined => {
  if (view.mode === "tasks") {
    const relativePath = directoryRelativeUrl(name, view.logDir);
    const decodedPath = decodeURIComponent(relativePath);
    return {
      id: name,
      name: decodedPath,
      url: tasksUrl(decodedPath, view.logDir),
    };
  }

  if (!isInDirectory(name, view.currentDir)) return undefined;

  const dirName = directoryRelativeUrl(view.currentDir, view.logDir);
  const relativePath = directoryRelativeUrl(name, view.currentDir);
  const fileOrFolderName = decodeURIComponent(rootName(relativePath));
  const path = join(
    decodeURIComponent(relativePath),
    decodeURIComponent(dirName)
  );
  return {
    id: fileOrFolderName,
    name: fileOrFolderName,
    url: logsUrl(path, view.logDir),
  };
};

/**
 * Map a listing row to the file item it displays as under `view`, or
 * `undefined` when the view has no file row for it: a retried run while
 * retried logs are hidden, or (folder mode) a file that isn't directly in
 * the current directory.
 *
 * This is the row-universe membership + identity function for the log list:
 * LogsPanel builds its items through it, and the listing query applies it to
 * database records, so the two can never disagree about which files are rows.
 */
export const fileLogItem = (
  logFile: LogListingRow,
  view: FileLogItemView
): FileLogItem | undefined => {
  if (!view.showRetriedLogs && logFile.retried) return undefined;
  const identity = fileLogIdentity(logFile.name, view);
  return identity === undefined
    ? undefined
    : { ...identity, type: "file", log: logFile };
};
